import type { IPropertyMember } from '@/domain/entities/property-member';
import { getApiEndpoints } from '@/services/api/endpoints';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type { PropertyMemberProfile } from './propertyMembersTypes';

export const fetchPropertyMemberProfile = createAppAsyncThunk<
  PropertyMemberProfile,
  { userID: string; profileType: 'homeowner' | 'business' }
>(
  'propertyMembers/fetchProfile',
  async ({ userID, profileType }, { extra: { config }, rejectWithValue }) => {
    try {
      const endpoint = `${getApiEndpoints().core.propertyMembers.profilesBase}/${profileType}/${encodeURIComponent(userID)}`;
      return await config.apiService.get<PropertyMemberProfile>(endpoint);
    } catch (error) {
      return rejectWithValue({
        message: error instanceof Error && error.message ? error.message : 'Failed to fetch member profile',
      });
    }
  },
  {
    condition: ({ userID }, { getState }) => {
      const id = userID.trim();
      const state = getState().propertyMembers;
      return Boolean(id && !state.fetchingProfilesByUserID[id] && !state.resolvedProfilesByUserID[id]);
    },
  },
);

function toPropertyKey(propertyID: string) {
  return propertyID.trim();
}

function propertyMembersPath(propertyID: string): string {
  return `${getApiEndpoints().core.propertyMembers.base}/${encodeURIComponent(propertyID)}/members`;
}

export const fetchPropertyMembers = createAppAsyncThunk<IPropertyMember[], { propertyID: string; force?: boolean }>(
  'propertyMembers/fetchByProperty',
  async ({ propertyID }, { extra: { config }, rejectWithValue }) => {
    const id = toPropertyKey(propertyID);

    try {
      return await config.apiService.get<IPropertyMember[]>(propertyMembersPath(id));
    } catch (error) {
      return rejectWithValue({
        message: error instanceof Error && error.message ? error.message : 'Failed to fetch property members',
      });
    }
  },
  {
    condition: ({ propertyID, force }, { getState }) => {
      const id = toPropertyKey(propertyID);
      if (!id) return false;
      if (force) return true;

      const state = getState().propertyMembers;
      if (state.fetchingByPropertyID[id]) return false;
      if (state.hasFetchedByPropertyID[id]) return false;
      return true;
    },
  },
);
